import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Smartphone, Share, Plus, CheckCircle2, ShieldCheck } from "lucide-react";
import easyserviceLogo from "@/assets/easyservice.png";
import "@khmyznikov/pwa-install";
import type { PWAInstallElement } from "@khmyznikov/pwa-install";

const APK_URL = "https://play.google.com/store/apps/details?id=com.samirgp.easyservice&hl=fr-FR";
const APK_VERSION = "1.0.0";
const APK_SIZE = "~3 Mo";

function isIos() {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function InstallPage() {
  const pwaInstallRef = useRef<PWAInstallElement | null>(null);
  const [installed, setInstalled] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    setIos(isIos());
    setInstalled(isStandalone());
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  const handlePwaInstall = () => {
    pwaInstallRef.current?.showDialog?.();
  };

  return (
    <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)]">
      <pwa-install
        ref={pwaInstallRef}
        manual-apple="true"
        manual-chrome="true"
        manifest-url="/manifest.webmanifest"
        name="EASYSERVICE EXPRESS"
        description="Recharge & retrait instantané"
        icon="/android-chrome-192x192.png"
        install-description="Ajoute EASYSERVICE EXPRESS à ton écran d'accueil pour un accès rapide."
      />

      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)",
        }}
      />

      <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-3xl lg:px-8">
          <Link
            to="/"
            aria-label="Retour"
            className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <h1 className="text-base font-semibold tracking-tight">Installer EASYSERVICE EXPRESS</h1>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[52ch] flex-1 px-4 py-6 lg:max-w-3xl lg:px-8">
        <div className="flex flex-col items-center text-center">
          <div className="grid size-16 place-items-center overflow-hidden rounded-2xl bg-card ring-1 ring-card-muted-ring shadow-lg">
            <img src={easyserviceLogo} alt="EASYSERVICE EXPRESS" className="size-full object-contain" />
          </div>
          <p className="mt-3 text-lg font-semibold tracking-tight">EASYSERVICE EXPRESS</p>
          <p className="mt-0.5 text-[12px] text-muted-foreground">Recharge & retrait instantané</p>
        </div>

        {installed ? (
          <div className="mt-8 rounded-2xl border border-brand-blue/30 bg-brand-blue/10 p-5 text-center">
            <CheckCircle2 className="mx-auto size-6 text-brand-blue" />
            <p className="mt-2 text-sm font-semibold">Application déjà installée</p>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Ouvre-la directement depuis ton écran d'accueil.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-8 text-center text-[13px] font-semibold text-foreground">
              Choisis ta méthode d'installation
            </p>

            {/* Option 1 : APK Android */}
            <section className="mt-4 rounded-2xl border border-[#34a853]/30 bg-[#e6f4ea] dark:bg-[#34a853]/10 p-4">
              <div className="flex items-start gap-3">
                <span className="grid size-11 shrink-0 place-items-center overflow-hidden rounded-full bg-card ring-1 ring-[#34a853]/20">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Google Play"
                    className="size-6 object-contain"
                  />
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">Google Play Store</p>
                    <span className="rounded-full bg-[#34a853] px-2 py-0.5 text-[10px] font-bold text-white uppercase tracking-wide">
                      Recommandé
                    </span>
                  </div>
                  <p className="mt-0.5 text-[12px] leading-relaxed text-foreground/70 dark:text-foreground/60">
                    Installation en 1 clic, mises à jour automatiques.
                  </p>
                </div>
              </div>
              <a
                href={APK_URL}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#34a853] py-3 text-sm font-semibold text-white transition hover:bg-[#2d9147]"
              >
                <Download className="size-4" /> Ouvrir Google Play
              </a>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[11px] text-[#1e8e3e] dark:text-[#4ade80]">
                <ShieldCheck className="size-3.5" /> Version vérifiée par Google
              </p>
            </section>

            {/* Option 2 : PWA */}
            <section className="mt-3 rounded-2xl bg-card-muted p-4 ring-1 ring-card-muted-ring">
              <div className="flex items-start gap-3">
                <span className="grid size-11 shrink-0 place-items-center rounded-full bg-sky-500/15 text-sky-300">
                  <Plus className="size-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Ajouter à l'écran d'accueil</p>
                  <p className="mt-0.5 text-[12px] leading-relaxed text-foreground/65">
                    Utilise EASYSERVICE EXPRESS directement depuis ton navigateur, comme une vraie app.
                  </p>
                </div>
              </div>

              {ios ? (
                <ol className="mt-4 space-y-2.5 rounded-xl bg-overlay p-3.5 text-[12px] leading-relaxed text-foreground ring-1 ring-card-muted-ring">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-muted text-[10px] font-bold text-foreground">1</span>
                    <span>
                      Touche <Share className="inline size-3.5 -mt-0.5" /> <b>Partager</b> dans la barre Safari.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-muted text-[10px] font-bold text-foreground">2</span>
                    <span>
                      Choisis <b>« Sur l'écran d'accueil »</b>.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-muted text-[10px] font-bold text-foreground">3</span>
                    <span>
                      Valide avec <b>Ajouter</b> — l'app apparaît sur ton écran.
                    </span>
                  </li>
                </ol>
              ) : (
                <>
                  <button
                    onClick={handlePwaInstall}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#001a4d] py-3 text-sm font-semibold text-white transition hover:bg-[#002366]"
                  >
                    Installer
                  </button>
                  <p className="mt-2 text-center text-[11px] text-text-subtle">
                    Non disponible dans ce navigateur — utilise Chrome ou télécharge l'APK.
                  </p>
                </>
              )}
            </section>
          </>
        )}

        <p className="mt-6 text-center text-[11px] text-text-subtle">
          EASYSERVICE EXPRESS — Recharge & retrait instantané
        </p>
      </main>
    </div>
  );
}

export default InstallPage;